import { ConditionChild, ConditionGroup, conditionGroupChildenKey } from 'editor/src/store/design/types';

export interface ConditionGroupRepairs {
  dangling: number;
  duplicate: number;
  malformed: number;
  resetBuckets: number;
}

export function addRepairs(total: ConditionGroupRepairs, repairs: ConditionGroupRepairs) {
  total.dangling += repairs.dangling;
  total.duplicate += repairs.duplicate;
  total.malformed += repairs.malformed;
  total.resetBuckets += repairs.resetBuckets;
}

export function hasRepairs({ dangling, duplicate, malformed, resetBuckets }: ConditionGroupRepairs) {
  return dangling + duplicate + malformed + resetBuckets > 0;
}

export function emptyRepairs(): ConditionGroupRepairs {
  return { dangling: 0, duplicate: 0, malformed: 0, resetBuckets: 0 };
}

/**
 * Designs saved by editor versions that generated colliding condition ids (GAPI-45047) can carry
 * children entries that are duplicated, or that point at a condition which no longer exists. Both
 * are invalid state that every consumer walking the children arrays would have to guard against,
 * so they are dropped when the design is loaded.
 *
 * This runs on untyped design data coming from the host, so it must never throw: failing to load a
 * design is worse than the corruption it repairs. Counts (never per-entry details) are returned so
 * the caller can report how many designs still arrive corrupted without unbounded payloads.
 */
function sanitizeConditionGroup(conditionGroup: ConditionGroup): ConditionGroupRepairs {
  const repairs = emptyRepairs();

  if (!conditionGroup.conditions || typeof conditionGroup.conditions !== 'object') {
    conditionGroup.conditions = {};
    repairs.malformed += 1;
  }

  if (!conditionGroup.children || typeof conditionGroup.children !== 'object') {
    conditionGroup.children = { [conditionGroupChildenKey]: [] };
    repairs.malformed += 1;
    return repairs;
  }

  Object.keys(conditionGroup.children).forEach((childrenKey) => {
    const children = conditionGroup.children[childrenKey];

    if (!Array.isArray(children)) {
      // malformed design data: a children bucket is always a list
      conditionGroup.children[childrenKey] = [];
      repairs.resetBuckets += 1;
      return;
    }

    const seen = new Set<string>();
    conditionGroup.children[childrenKey] = children.filter((child: ConditionChild) => {
      if (!child || typeof child !== 'object') {
        repairs.malformed += 1;
        return false;
      }

      if (child.type === 'condition' && !conditionGroup.conditions[child.id]) {
        repairs.dangling += 1;
        return false;
      }

      const key = `${child.type}:${child.id}`;
      if (seen.has(key)) {
        repairs.duplicate += 1;
        return false;
      }
      seen.add(key);

      return true;
    });
  });

  if (!Array.isArray(conditionGroup.children[conditionGroupChildenKey])) {
    // every consumer assumes the top-level bucket exists
    conditionGroup.children[conditionGroupChildenKey] = [];
    repairs.malformed += 1;
  }

  return repairs;
}

export default sanitizeConditionGroup;
